import assert from 'assert'

import { InputValueContentNode } from '../../../../../packages/cms/dist/esm/ContentNode/ContentNode.types'
import { useAppStore } from '../../store/appStore/appStore'
import { UpdateQueryOptionsV2 } from '../../store/appStore/queryStore/querySlice.actions'

export type UseWidgetQueryOptions = {
  /**
   * Set to create a query that can be shared by this id.
   */
  queryId: string
}

export const useWidgetQuery = ({ queryId }: UseWidgetQueryOptions) => {
  'use no memo'
  assert(queryId, `Query ${queryId} not found`)

  const {
    queryNode,
    updateQuery,
    refetchQuery,
    updateQueryInputValue,
    updateVariables,
    resetPagination,
  } = useAppStore(({ queryStore }) => ({
    queryNode: queryStore.getQuery(queryId),
    updateQuery: queryStore.updateQuery,
    refetchQuery: queryStore.refetchQuery,
    updateQueryInputValue: queryStore.updateQueryInputValue,
    updateVariables: queryStore.updateVariables,
    resetPagination: queryStore.resetPagination,
  }))

  assert(queryNode, `Query ${queryId} queryNode not found`)

  return {
    ...queryNode?.apolloState,
    queryNode: queryNode,
    isLoading: queryNode?.apolloState?.loading,
    paginated: queryNode?.paginated ?? false,

    /**
     * Updates the query and awaits the result.
     */
    updateQuery: (options: Omit<UpdateQueryOptionsV2, 'queryId'>) => {
      if (!queryNode) return

      return updateQuery({
        queryId: queryId!,
        ...options,
      })
    },
    updateQueryInputValue: (inputValueContentNode: InputValueContentNode) => {
      if (!queryNode) return
      return updateQueryInputValue(queryId, inputValueContentNode)
    },
    updateVariables: (variables: Record<string, unknown>) => {
      if (!queryNode) return
      return updateVariables(queryId, variables)
    },
    /**
     * Refetches the query
     */
    refetch: () => {
      if (!queryId || !queryNode) return
      return refetchQuery(queryId)
    },
    /**
     * Resets the query data and Apollo state
     */
    resetPagination: () => {
      if (!queryId || !queryNode) return
      return resetPagination(queryId)
    },
  }
}
